import React, { useEffect, useState, useMemo } from 'react'
import { inject } from 'mobx-react'
import { observer } from 'mobx-react-lite'
import { CreateProduct, CreateChildProduct } from '../ProductStore'
import { CurrentCompanyStore } from '../../../../../stores/CurrentCompanyStore'
import { CurrentUserStore } from '../../../../../stores/CurrentUserStore'
import { Button, message } from 'antd-v4'
import { GrPrice, parseErrorMsg } from '../../../../../utilities/helpers'
import { PermissionNames } from '@getgreenline/homi-shared'
import { DiscountModels } from '@getgreenline/products'
import { DeleteOutlined, EditOutlined } from '@ant-design/icons'
import { browserHistory } from 'react-router'
import { Routes } from '../../../../../constants/Routes'
import { NoPermissionsResult } from '../../../../../components/NoPermissionsResult'
import Table, { ColumnProps } from 'antd/lib/table'
import { Empty } from 'antd'
import { DiscountFilter } from '../../Discounts/DiscountFilter'
import { FilterGroup } from './FilterGroup'
import { BlazeButton } from '../../../../../components/CustomButtons/BlazeButton'
import { availableDiscounts } from '../../../../../utilities/DiscountHelpers'
import { THEME_COLORS } from '@getgreenline/shared'

interface InjectedProps {
  currentCompanyStore?: CurrentCompanyStore
  currentUserStore?: CurrentUserStore
}

interface Props extends InjectedProps {
  product: CreateProduct | CreateChildProduct
}

interface DiscountsTableRecord {
  key: number
  name: string
  locations: JSX.Element
  applyType: string
  amount: JSX.Element
  pricing: JSX.Element
}

export const DiscountsTab: React.FC<Props> = inject(
  'currentCompanyStore',
  'currentUserStore',
)(
  observer(({ currentCompanyStore, currentUserStore, product }) => {
    useEffect(() => {
      const fetchData = async () => {
        try {
          await getDiscounts()

          if (!currentCompanyStore?.locations) {
            await currentCompanyStore?.getLocations()
          }

          if (!currentCompanyStore?.globalLocations) {
            await currentCompanyStore?.getGlobalLocations()
          }
        } catch (error) {
          message.error(parseErrorMsg(error) || 'An error occurred while loading product discounts')
        }
      }

      fetchData()
    }, [])

    const filter = useMemo(() => {
      return new DiscountFilter()
    }, [])

    const [discounts, setDiscounts] = useState<DiscountModels.IDiscount[]>([])
    const [isLoading, setIsLoading] = useState(false)

    const canViewDiscounts = currentUserStore?.hasPermissionAtEntity(
      PermissionNames.CAN_VIEW_DISCOUNTS,
      currentCompanyStore!.company!.id,
    )
    const canEditDiscounts = currentUserStore?.hasPermissionAtEntity(
      PermissionNames.CAN_EDIT_DISCOUNTS,
      currentCompanyStore!.company!.id,
    )

    const getDiscounts = async () => {
      if (product.id === null) return
      if (!currentCompanyStore) return

      const networkObject = filter.networkObject

      setIsLoading(true)
      await currentCompanyStore?.productStore!.getDiscounts({
        searchFilter: {
          ...networkObject,
        },
        permissions: {
          canUseExternalLoyalty: !!currentCompanyStore?.canUseExternalLoyalty,
          canAccessLoyalty: !!currentCompanyStore?.company?.permissions.canAccessLoyalty,
        },
      })

      const discounts = availableDiscounts(currentCompanyStore, product)

      setDiscounts(discounts)
      setIsLoading(false)
    }

    const renderLocations = (locationIds: number[]) => {
      if (locationIds.length === 0) {
        return <div className='ml-1'>All</div>
      } else {
        return (
          <ul className='pl-1 mb-1'>
            {locationIds.map((id) => {
              const globalLocation = currentCompanyStore?.globalLocations?.find(
                (location) => location.id === id,
              )
              const allowedLocation = currentCompanyStore?.locations?.find(
                (location) => location.id === id,
              )
              return (
                <li
                  key={id}
                  title={globalLocation?.name}
                  style={allowedLocation ? { opacity: 1 } : { opacity: 0.5 }}
                >
                  {globalLocation?.name}
                </li>
              )
            })}
          </ul>
        )
      }
    }

    const getAppliedTo = (applyType: DiscountModels.ApplyType) => {
      if (applyType === 'products') {
        return 'Product'
      } else if (applyType === 'cart') {
        return 'Entire cart'
      } else {
        return '-'
      }
    }

    const renderPricing = (discount: DiscountModels.IDiscount) => {
      let discountedPrice = 0

      if (discount.type === DiscountModels.DiscountTypes.FIXED && product.price) {
        discountedPrice = product.price - discount.amount
      }

      if (discount.type === DiscountModels.DiscountTypes.PERCENT && product.price) {
        discountedPrice = product.price - Math.round((discount.amount / 100) * product.price)
      }

      return (
        <>
          <div className='d-flex justify-content-between'>
            <s>{GrPrice.renderDollarString(product.price)}</s>
            <Button
              onClick={() => onClickEditDiscount(discount.id)}
              size='small'
              className='mb-1'
              icon={<EditOutlined />}
            />
          </div>
          <div className='d-flex justify-content-between'>
            <span style={{ color: THEME_COLORS.BRAND.retailAlt }}>
              {GrPrice.renderDollarString(discountedPrice)}
            </span>
            <Button
              onClick={() => onClickRemoveDiscount(discount)}
              size='small'
              icon={<DeleteOutlined />}
            />
          </div>
        </>
      )
    }

    const onClickEditDiscount = (discountId: number) => {
      if (!canEditDiscounts) {
        return message.error(
          `Your account does not have the "${PermissionNames.CAN_EDIT_DISCOUNTS}" permission. Please ask your administrator for access.`,
        )
      }

      if (!currentCompanyStore || !currentCompanyStore.company) return

      window.open(
        Routes.DASHBOARD_PRODUCTS_EDIT_DISCOUNT(currentCompanyStore.company.id, discountId),
        '_blank',
      )
    }

    const onClickRemoveDiscount = async (discountProfile: DiscountModels.IDiscount) => {
      if (!canEditDiscounts) {
        return message.error(
          `Your account does not have the "${PermissionNames.CAN_EDIT_DISCOUNTS}" permission. Please ask your administrator for access.`,
        )
      }

      try {
        const discountObj = new DiscountModels.CreateOrEditDiscount(discountProfile)
        const allProductsAndCategoriesError = `"${discountProfile.name}" is applied to all products and categories. Please go to Discounts to edit`
        const categoriesError = `"${discountProfile.name}" is applied to one or more categories. Please go to Discounts to edit`
        const lastProductError = `"${discountProfile.name}" has no other products assigned to it. Please go to Discounts to edit`

        if (discountObj.categories.length === 0 && discountObj.products.length === 0) {
          throw new Error(allProductsAndCategoriesError)
        }

        discountObj.products.forEach((product) => {
          if (product.type === 'exclude') {
            throw new Error(allProductsAndCategoriesError)
          }
        })

        discountObj.categories.forEach((category) => {
          if (category.type === 'exclude') {
            throw new Error(allProductsAndCategoriesError)
          }
        })

        if (discountObj.categories.length !== 0) {
          throw new Error(categoriesError)
        }

        if (discountObj.products.length === 1) {
          throw new Error(lastProductError)
        }

        if (
          window.confirm(
            `Are you sure you want to remove "${discountProfile.name}" from this product?`,
          )
        ) {
          discountObj.removeProduct(discountObj.products.findIndex((obj) => obj.id === product.id))

          await currentCompanyStore?.productStore?.editDiscount(discountObj)
          await getDiscounts()

          message.success(`"${discountProfile.name}" removed from this product`)
        }
      } catch (error) {
        message.error(parseErrorMsg(error) || 'An error occurred while removing the discount')
      }
    }

    const onClickAddDiscount = () => {
      if (!canEditDiscounts) {
        return message.error(
          `Your account does not have the "${PermissionNames.CAN_EDIT_DISCOUNTS}" permission. Please ask your administrator for access.`,
        )
      }

      if (!currentCompanyStore || !currentCompanyStore.company) return

      browserHistory.push(Routes.DASHBOARD_PRODUCTS_ADD_DISCOUNT(currentCompanyStore.company.id))
    }

    const tableDataSource: DiscountsTableRecord[] = discounts.map((discount) => {
      return {
        key: discount.id,
        name: discount.name,
        locations: renderLocations(discount.locationIds),
        applyType: getAppliedTo(discount.applyType),
        amount: (
          <div className='ml-3'>
            {discount.type === DiscountModels.DiscountTypes.FIXED
              ? `$${discount.amount / 100}`
              : `${discount.amount}%`}
          </div>
        ),
        pricing: renderPricing(discount),
      }
    })

    const columns: ColumnProps<DiscountsTableRecord>[] = [
      {
        title: 'Name',
        dataIndex: 'name',
        key: 'name',
        ellipsis: true,
        className: 'pl-3',
        width: 100,
      },
      {
        title: 'Locations',
        dataIndex: 'locations',
        key: 'locations',
        ellipsis: true,
        width: 100,
      },
      {
        title: 'Applied to',
        dataIndex: 'applyType',
        key: 'applyType',
        width: 100,
      },
      {
        title: 'Amount',
        dataIndex: 'amount',
        key: 'amount',
        width: 100,
      },
      {
        title: 'Pricing',
        dataIndex: 'pricing',
        key: 'pricing',
        className: 'pr-3',
        ellipsis: true,
        width: 100,
      },
    ]

    return (
      <>
        {canViewDiscounts ? (
          <>
            <FilterGroup filter={filter} onApplyFilter={getDiscounts} />
            <Table
              rowKey={(row) => row.key.toString()}
              columns={columns}
              dataSource={tableDataSource}
              size='small'
              pagination={false}
              loading={isLoading}
              scroll={{ x: true }}
              locale={{
                emptyText: <Empty description='This product has no discounts' />,
              }}
              className='discounts-tab-table mr-2 mb-3'
            />
            <BlazeButton
              buttonProps={{
                icon: 'plus',
                onClick: () => onClickAddDiscount(),
              }}
              titleProps={{
                title: 'Add discount',
              }}
            />
          </>
        ) : (
          <NoPermissionsResult permissionName={PermissionNames.CAN_VIEW_DISCOUNTS} />
        )}
      </>
    )
  }),
)
